import * as React from 'react';
import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';
import { WebClientListResourceFetcher } from '~commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';

import { GraphQLTagValuesQuery } from '~src/catalog/fetchers/GraphQLTagValuesQuery';
import { AngularDirective } from '~common/react/components/AngularDirective';
import { FlowOutputStepRuns } from '~src/flow/resources/FlowOutputStepRuns';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { FilterPanelTypeaheadScope, IFilterPanelContainerScope } from '~src/ts/controllers/FilterPanelCtrl';
import { AngularContext } from '~src/ts/factories/AngularContextFactory';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { Filter } from '~src/ts/helpers/Filter';
import { ServerFilterClauses } from '~src/ts/helpers/ServerFilterClauses';
import { ServerApiMapper } from '~src/ts/helpers/ServerModelHelper';
import { getExplorableItemDefinition } from '~src/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ContentFilterKeyName, ContentFiltersKeys } from '~src/ts/models/ContentFilters';
import { ExplorableItemType } from '~src/ts/models/ContentType';
import { FlowOutputStepRunContentType, MixedContentType } from '~src/ts/models/ContentTypes';
import { FieldValuesQuery } from '~src/ts/resources/FieldValuesQuery';
import { GetFieldValuesForMixedContentRequest, GetFieldValuesRequest } from '~src/ts/ServerApi/requests/GetFieldValuesRequest';
import { FilterFieldNames, IFilterClause } from '~src/ts/ServerApi/types/IFilterClause';
import { FilterPanelService, IFilterDefinition } from '~content/services/FilterPanelService';
import { removeFilterClauseFromFilters } from '../../actions/spaces/filters/FilterHelpers';
import { getFilterClausesForTypes } from '../../actions/spaces/filters/getFilterClausesForTypes';
import { FilterMap } from '../../state/ContentExplorerState';
import {
  isCollectionsSpace,
  isBackgroundJobsSpace, isSingleCollectionSpace,
  isFavoritesSpace,
  isFlowSpace,
  isRecentsSpace,
  isSiteSpace,
  isSiteUserCollectionsSubspace,
  isSiteUserSpace,
  isSomeKindaSearchSpace,
  SpaceInfo,
  NullProjectIdForNonProjects,
  isMyCollectionsSubspace,
} from '../../state/SpaceInfo';
import { CollectionFieldValuesQuery, CollectionItemsFieldValuesQuery } from '../Collections/ResourceQuery/FieldValuesQuery';

export interface AdvancedFilterPanelProps {
  readonly angularContext: AngularContext;
  readonly mixedContentFilterMap: FilterMap;
  readonly spaceInfo: SpaceInfo;
  readonly shownContentTypes: ReadonlyArray<ExplorableItemType>;
  readonly scopeReference?: (scope: AdvancedFilterPanelScope) => void;
}

const template = `<div ng-controller="FilterPanelCtrl" ng-include="'advancedFilterPanel.html'"></div>`;

export type AdvancedFilterPanelScope = IFilterPanelContainerScope & FilterPanelTypeaheadScope & { contentType: string };

export class AdvancedFilterPanel extends React.Component<AdvancedFilterPanelProps> {

  public static TestId = 'advanced-filter-panel';

  private sessionManagementService: SessionManagementService;
  private filterPanelScope: AdvancedFilterPanelScope = null;

  private static FilterKeysForTopLevelOnly = [
    ContentFiltersKeys.project,
    ContentFiltersKeys.allProjects
  ];

  public constructor(props: AdvancedFilterPanelProps) {
    super(props);
    this.sessionManagementService = getSessionManagementServiceInstance();
  }

  public componentWillReceiveProps(nextProps: AdvancedFilterPanelProps): void {
    // We need to update the scope whenever our showContentTypes are updated to make sure
    // we render the correct filters in the angular pane
    // TODO future optimizations: Should we move this into render? Should we memoize its results?
    // Can this be a pure component after all?
    const spaceInfoChanged = nextProps.spaceInfo.spaceType !== this.props.spaceInfo.spaceType
      || nextProps.spaceInfo.subspaceName !== this.props.spaceInfo.subspaceName
      || nextProps.spaceInfo.item.id !== this.props.spaceInfo.item.id;

    if (!shallowEqual(nextProps.shownContentTypes, this.props.shownContentTypes) ||
          spaceInfoChanged) {
      _.defer(() => this.filterPanelScope.$apply((scope: AdvancedFilterPanelScope) => {
        scope.filterDefinitions = this.getAdvancedFiltersForShownContentTypes();
        scope.getFieldValueQuery = this.getFieldValueQuery();
        scope.contentType = (nextProps.shownContentTypes.length === 1) ? nextProps.shownContentTypes[0] : MixedContentType.name;
        scope.hideSearch = this.shouldHideSearchForSpaceInfo();
      }));
    }
  }

  public componentDidMount() {
    if (this.props.scopeReference) {
      this.props.scopeReference(this.filterPanelScope);
    }
  }

  private isShowingOneContentType(): boolean {
    return this.props.shownContentTypes.length === 1;
  }

  private isShowingTableContent(): boolean {
    return this.props.shownContentTypes.every(t => t === 'table' || t === 'tableWithCrass');
  }

  private isShowingDatabaseContent(): boolean {
    return this.props.shownContentTypes.every(t => t === 'database' || t === 'databaseWithCrass');
  }

  private getAdvancedFiltersForShownContentTypes(): IFilterDefinition<any>[] {
    let filterKeys: ContentFilterKeyName[];

    if (this.isShowingOneContentType()) {
      filterKeys = getExplorableItemDefinition(this.props.shownContentTypes[0]).contentFilterKeys;
    } else {
      // no types or multiple types - show mixed content filters
      filterKeys = MixedContentType.filterKeys;
      if (!isRecentsSpace(this.props.spaceInfo) && !isSingleCollectionSpace(this.props.spaceInfo)) {
        // recents page and collection supports the tags filter for mixed content
        filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.tag);
      }
    }

    if (isBackgroundJobsSpace(this.props.spaceInfo)) {
      // The showSystemJobs filter is only available for server admin users
      if (!this.sessionManagementService.isSessionServerAdmin()) {
        filterKeys = _.without(filterKeys, 'showSystemJobs');
      }
    }

    // If not at top level (so project place), remove the top-level-only keys
    if (!isSiteSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => _.contains(AdvancedFilterPanel.FilterKeysForTopLevelOnly, key));
    }

    // remove favorites filter when on favorites space or for collections when useCollectionsFavorites is off
    if (isFavoritesSpace(this.props.spaceInfo) || this.shouldHideFavoritesFilterForCollections()) {
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.favorite);
    }

    // remove recents filter when on recents space
    if (isRecentsSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.recent);
    }

    if (isFlowSpace(this.props.spaceInfo)) {
      filterKeys = FlowOutputStepRunContentType.filterKeys;
    }

    if (isSiteUserSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.owner);
    }

    if (isMyCollectionsSubspace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, key => key === ContentFiltersKeys.owner);
    }

    if (isSingleCollectionSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, key =>
        // See Feature-level TFS 1172357 for all ga-silver related stories
        key === ContentFiltersKeys.hasAlert
        // Datasource specific filters (collections ga-silver)
        || key === ContentFiltersKeys.connectionType
        || key === ContentFiltersKeys.includeLiveToDataEngine
        || key === ContentFiltersKeys.extractStatus // Live or Extract
        || key === ContentFiltersKeys.showEmbedded  // Data source dropdown
        || key === ContentFiltersKeys.serverPort
        // Workbook specific filters
        || key === ContentFiltersKeys.workbookConnFilter
        || key === ContentFiltersKeys.recent
      );
    }

    return FilterPanelService.getFilterDefinitions(filterKeys);
  }

  private getFieldValueQueryForShownContentTypes(filterField: string): any {
    if (this.isShowingDatabaseContent()) {
      return new GraphQLTagValuesQuery('database');
    } else if (this.isShowingTableContent()) {
      return new GraphQLTagValuesQuery('table');
    }

    // Collections have a different API for field values -> directly to CS.
    if (isSingleCollectionSpace(this.props.spaceInfo)) {
      return new CollectionItemsFieldValuesQuery(this.props.spaceInfo.item.luid, filterField);
    }

    if (isCollectionsSpace(this.props.spaceInfo) || isSiteUserCollectionsSubspace(this.props.spaceInfo)) {
      return new CollectionFieldValuesQuery(filterField);
    }

    if (this.isShowingOneContentType()) {
      const fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
      const resourceType = getExplorableItemDefinition(this.props.shownContentTypes[0]).fieldValueResourceType;
      return new FieldValuesQuery(fieldValuesFetcher, filterField, resourceType);
    } else {
      // use mixed content api
      const fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesForMixedContentRequest, ServerApiMapper.fieldValues);
      return new FieldValuesQuery(fieldValuesFetcher, filterField);
    }
  }

  private getFieldValueQuery(): (filterField: string) => FieldValuesQuery {
    return (filterField: string) => {
      if (this.props.spaceInfo.subspaceName === 'runHistory') {
        let filterClauses: IFilterClause[] = _.chain(this.props.mixedContentFilterMap)
                                              .pick(FlowOutputStepRunContentType.filterKeys)
                                              .values()
                                              .compact()
                                              .value();

        filterClauses = removeFilterClauseFromFilters(filterClauses, FilterFieldNames.flowOutputStepName);
        filterClauses.push(Filter.stringFieldEq(FilterFieldNames.flowId, this.props.spaceInfo.item.id));

        return FlowOutputStepRuns.valuesForField(FilterFieldNames.flowOutputStepName).withFilters(filterClauses);
      }

      let filtersForType: IFilterClause[] = getFilterClausesForTypes(
          this.props.mixedContentFilterMap,
          this.props.shownContentTypes,
          this.props.shownContentTypes,
          isSiteUserSpace(this.props.spaceInfo) ? NullProjectIdForNonProjects : this.props.spaceInfo.item.id,
          this.props.spaceInfo
      );

      // need to add the project filter clause so we don't get everything on the site/server for mixed content
      if (!this.isShowingOneContentType() && !isSingleCollectionSpace(this.props.spaceInfo)) {
        const projectIdFilter = ServerFilterClauses.serverFilterClauses.project(this.props.spaceInfo.item.id);
        filtersForType.push(projectIdFilter);
      }

      // add the favorites=true filter when in favorites space
      if (isFavoritesSpace(this.props.spaceInfo)) {
        const favoritesFilter = ServerFilterClauses.serverFilterClauses.favorite('true');
        filtersForType.push(favoritesFilter);

      } else if (isRecentsSpace(this.props.spaceInfo)) {
        const recentsFilter = ServerFilterClauses.serverFilterClauses.recent('true');
        filtersForType.push(recentsFilter);
      }

      // if we are searching for projects or in the site - projects space we want to get field values for all projects not just top level ones
      if (isSomeKindaSearchSpace(this.props.spaceInfo) && this.props.shownContentTypes.length === 1 && this.props.shownContentTypes[0] === 'project') {
        filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
      }

      if (isSiteSpace(this.props.spaceInfo) && this.props.spaceInfo.subspaceName === 'project') {
        filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
      }

      if (isSiteUserSpace(this.props.spaceInfo)) {
        filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
        filtersForType.push(ServerFilterClauses.serverFilterClauses.owner(this.props.spaceInfo.item.id));
      }

      if (isMyCollectionsSubspace(this.props.spaceInfo)) {
        const sessionManagementService = getSessionManagementServiceInstance();
        const userId = sessionManagementService.sessionUserId();
        filtersForType.push(ServerFilterClauses.serverFilterClauses.owner(userId));
      }

      // We want to remove the field we are looking for so we can get all the possibilities back for it.
      // If the filter has no field, it obviously does not match the current field we want, so keep it.
      const filterClausesWithoutQueriedField = removeFilterClauseFromFilters(filtersForType, filterField);

      const fieldValuesQuery: FieldValuesQuery = this.getFieldValueQueryForShownContentTypes(filterField);
      return fieldValuesQuery.withFilters(filterClausesWithoutQueriedField).withMatchText('');
    };
  }

  private readonly setupScopeForFilterPanel = (scope: ng.IScope): AdvancedFilterPanelScope => {
    const childScope = scope as AdvancedFilterPanelScope;
    this.filterPanelScope = childScope;

    childScope.filterDefinitions = this.getAdvancedFiltersForShownContentTypes();
    childScope.filterControls = {};

    childScope.getFieldValueQuery = this.getFieldValueQuery();

    childScope.contentType = this.isShowingOneContentType() ? this.props.shownContentTypes[0] : MixedContentType.name;

    childScope.hideSearch = this.shouldHideSearchForSpaceInfo();

    return childScope;
  }

  private shouldHideSearchForSpaceInfo(): boolean {
    return isSomeKindaSearchSpace(this.props.spaceInfo) || isBackgroundJobsSpace(this.props.spaceInfo)
      || (isCollectionsSpace(this.props.spaceInfo) && !FeatureFlags.collectionsGAEnabled)
      || (isSiteUserCollectionsSubspace(this.props.spaceInfo) && !FeatureFlags.collectionsGAEnabled);
  }

  private shouldHideFavoritesFilterForCollections(): boolean {
    return !FeatureFlags.useCollectionsFavorites &&
      (isCollectionsSpace(this.props.spaceInfo) || isSiteUserCollectionsSubspace(this.props.spaceInfo));
  }

  public render(): JSX.Element {
    return (
      <AngularDirective template={template}
        angularContext={this.props.angularContext}
        setupScope={this.setupScopeForFilterPanel}
      />
    );
  }
}
